import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import RulesCard from "@/components/ats/RulesCard";
import { api, errMsg } from "@/lib/api";

export default function FileCheck({ jobId }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { api.get(`/jobs/${jobId}/file-check`).then(({ data }) => setR(data)); }, [jobId]);
  const up = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await api.post(`/jobs/${jobId}/file-check`, fd); setR(data); toast.success("File checked"); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  const parseFails = r?.parse.filter((p) => !p.pass) || [];
  return (
    <div className="card-surface space-y-3" data-testid="file-check">
      <div className="label-xs">Check the Jobright file</div>
      <p className="text-xs text-slate-400">Upload the PDF you exported from Jobright. The app reads it like an ATS and checks it against your picks and all JDs.</p>
      <input ref={ref} type="file" accept=".pdf,.docx" className="hidden" onChange={up} data-testid="file-check-input" />
      <button data-testid="file-check-upload" onClick={() => ref.current?.click()} disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 text-sm px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}{busy ? "Reading file…" : r ? "Check another file" : "Upload Jobright PDF"}</button>
      {r && (
        <div className="space-y-3 text-sm" data-testid="file-check-result">
          <div className="text-xs text-slate-500 truncate">{r.filename}</div>
          <div className="grid grid-cols-2 gap-2">
            <div><div className="label-xs">Keyword match</div><div className="text-xl font-bold font-display" data-testid="file-check-match">{r.match_pct}%</div></div>
            <div><div className="label-xs">Median per JD</div><div className="text-xl font-bold font-display">{r.per_jd.median}%</div></div>
            <div><div className="label-xs">Fields read</div><div className={`text-xl font-bold font-display ${parseFails.length ? "text-amber-400" : "text-emerald-400"}`} data-testid="file-check-parse">{r.parse.length - parseFails.length}/{r.parse.length}</div></div>
            <div><div className="label-xs">Title match</div><div className="text-xl font-bold font-display">{r.per_jd.title_match_pct}%</div></div>
          </div>
          {parseFails.length > 0 && <div className="text-xs text-rose-300">Not found in the file: {parseFails.map((p) => p.check).join("; ")}</div>}
          {r.missing_bullets.length > 0 && <div className="text-xs text-amber-200" data-testid="file-check-missing-bullets">{r.missing_bullets.length} picked bullet(s) not found in the file: {r.missing_bullets.map((b) => `"${b.slice(0, 60)}…"`).join(" ")}</div>}
          <RulesCard rules={r.rules} title="File checks" testid="file-check-rules" />
        </div>
      )}
    </div>
  );
}
